import { createContext, useContext, useEffect, useState } from "react";
import { useCurrentUserQuery } from "../queries/use-current-user-query";
import { useTrackUserAnalytics } from "../mutations/use-track-user-analytics";
import { useAuthStateChange } from "../hooks/use-auth-state-change";

const emptyContext = {
  loading: true,
  user: null as any,
  tenantId: "",
  developerMode: false,
};

const TenantContext = createContext<{
  loading: boolean;
  user: any;
  tenantId: string;
  developerMode: boolean;
}>(emptyContext);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

export function TenantProvider({ children }: { children: JSX.Element }) {
  const { data: user, isLoading } = useCurrentUserQuery();
  const { mutate: trackAnalytics } = useTrackUserAnalytics();

  const [trackedUser, setTrackedUser] = useState<boolean>(false);

  useAuthStateChange();

  useEffect(() => {
    if (user && !trackedUser) {
      trackAnalytics(user);
      setTrackedUser(true);
    }
  }, [user, trackAnalytics, trackedUser]);

  return (
    <TenantContext.Provider
      value={{
        loading: isLoading,
        user,
        tenantId: user?.company?.id || "",
        developerMode: user?.is_developer || false,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export { TenantContext };
